import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Home.scss';

import Feed from '../Feed/Feed';
import Headline from '../Headline/Headline';
import FashionDiscovery from './FashionDiscovery';
import PopularBrands from './PopularBrands';
import HomeBannerCarousel from './HomeBannerCarousel';
import FeedbackBanner from '../Feedback/FeedbackBanner';
import FeedbackModal from '../Feedback/FeedbackModal';
import HomeChatsPanel from './HomeChatsPanel';
import HomeNotificationsPanel from './HomeNotificationsPanel';
import searchIcon from '../Header/img/search.png';

import { Helmet } from 'react-helmet-async';

// Desktop 3-column shell breakpoint. Below this the page is the existing
// single-column mobile/tablet layout, unchanged.
const DESKTOP_SHELL_BREAKPOINT = 1100;

function useIsDesktopShell(breakpoint = DESKTOP_SHELL_BREAKPOINT) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= breakpoint : false
  );

  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const onChange = (e) => setIsDesktop(e.matches);
    try {
      mql.addEventListener('change', onChange);
    } catch {
      mql.addListener(onChange);
    }
    setIsDesktop(mql.matches);
    return () => {
      try {
        mql.removeEventListener('change', onChange);
      } catch {
        mql.removeListener(onChange);
      }
    };
  }, [breakpoint]);

  return isDesktop;
}

const Home = () => {
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // "Служба турботи" pill: the same FeedbackModal (idea / problem / tech support) the mobile app opens
  const [careOpen, setCareOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');
  const isDesktopShell = useIsDesktopShell();

  // Same navigate-on-submit flow as the desktop header's search box
  // (Header.js submitSearch): no separate search state/logic here, this
  // just hands the query to the existing /search?q= route + SearchResults.
  // `state: { autofocus: true }` tells SearchResults to focus its own main
  // input on landing, so typing continues there instead of on this bar.
  const submitMobileSearch = (e) => {
    e.preventDefault();
    const q = mobileSearchValue.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search', {
      state: { autofocus: true },
    });
  };

  // Tapping this bar while it's still empty jumps straight to SearchResults
  // (focused there) instead of opening the keyboard here first — it doesn't
  // run a search itself. Once something's typed, focus no longer redirects,
  // so editing/submitting on Home keeps working exactly as before.
  const handleMobileSearchFocus = () => {
    if (!mobileSearchValue.trim()) {
      navigate('/search', { state: { autofocus: true } });
    }
  };

  // Brand/entity discoverability (SEO Phase 2, Phase F): the homepage had
  // zero structured data at all — nothing telling a crawler or an
  // LLM-facing indexer what "Cvanto" the entity actually is. WebSite +
  // Organization here state only real, verifiable facts (name, url, the
  // real favicon as logo) — no sameAs (no confirmed official social
  // profile URLs to cite) and no invented follower counts/ratings/claims.
  // The description below paraphrases the platform's own stated
  // positioning, not a new marketing claim being introduced here.
  //
  // alternateName (SEO Phase G, 2026-09): helps Google associate the
  // Cyrillic transliteration "Кванто"/"кванто" with this entity when
  // someone searches that spelling instead of the Latin brand name.
  //
  // Brand/entity SEO (2026-09, follow-up): "cvanto" already ranks; "кванто"
  // did not surface this site at all (Google reads it as a different word/
  // entity, e.g. "Quanto"). alternateName in JSON-LD alone wasn't enough —
  // the parenthetical is added here, ONCE, in the single highest-weight
  // on-page signal (the title tag), with Cvanto still leading as the
  // canonical name. Deliberately not repeated on every page/title — see
  // About.js for the other of the "1-2 key pages" this appears on, per the
  // brief's own explicit anti-stuffing constraint.
  const HOME_TITLE = "Cvanto (Кванто) — оголошення, послуги та спеціалісти в Україні";
  const HOME_DESCRIPTION =
    "Cvanto (Кванто) — соціальний маркетплейс України: купівля, продаж, обмін і безкоштовна віддача речей та послуг, спілкування в тематичних спільнотах.";

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "name": "Cvanto",
        "alternateName": ["Кванто", "кванто"],
        "url": "https://cvanto.com",
        "description": HOME_DESCRIPTION,
      },
      {
        "@type": "Organization",
        "name": "Cvanto",
        "alternateName": ["Кванто", "кванто"],
        "url": "https://cvanto.com",
        "logo": "https://cvanto.com/apple-touch-icon.png",
      },
    ],
  };

  return (
    <div className="home">
      <Helmet>
        <title>{HOME_TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <link rel="canonical" href="https://cvanto.com/" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cvanto" />
        <meta property="og:title" content={HOME_TITLE} />
        <meta property="og:description" content={HOME_DESCRIPTION} />
        <meta property="og:url" content="https://cvanto.com/" />
        <meta property="og:image" content="https://cvanto.com/web-app-manifest-512x512.png" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={HOME_TITLE} />
        <meta name="twitter:description" content={HOME_DESCRIPTION} />
        <meta name="twitter:image" content="https://cvanto.com/web-app-manifest-512x512.png" />

        <script type="application/ld+json">
          {JSON.stringify(homeStructuredData)}
        </script>
      </Helmet>

      <div className="home__center">
      <form
        className="home__mobile-search"
        onSubmit={submitMobileSearch}
        role="search"
      >
        <img src={searchIcon} alt="" width={18} height={18} decoding="async" />
        <input
          type="search"
          className="home__mobile-search-input"
          placeholder="Пошук на Cvanto"
          value={mobileSearchValue}
          onChange={(e) => setMobileSearchValue(e.target.value)}
          onFocus={handleMobileSearchFocus}
          aria-label="Пошук на Cvanto"
        />
      </form>

      <Headline />

      {/* Final Home structure (2026-09 production polish pass): Search →
          Stories → promo/ad carousel → Women/Men/Kids → Shopping
          departments → Popular brands → shopping listings feed. */}
      {/* Home's permanent promo/ad slot — same homepage_banner_top AdSlot
          infrastructure (GET /api/ads/serve, impression/click tracking),
          carousel-capable: Cvanto's own system promo slide is always
          present, real admin campaigns for this placement append after it.
          See HomeBannerCarousel.js. */}
      <HomeBannerCarousel />

      <FashionDiscovery />

      <div className="home__quick-links">
        <Link to="/categories" className="home__quick-link">
          <svg className="home__quick-link-icon home__quick-link-icon--grid" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M4 4h7v7H4V4Zm9 0h7v7h-7V4ZM4 13h7v7H4v-7Zm9 0h7v7h-7v-7Z" />
          </svg>
          <span>Інші категорії</span>
        </Link>

        <button type="button" className="home__quick-link" onClick={() => setCareOpen(true)}>
          <svg className="home__quick-link-icon home__quick-link-icon--heart" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 2.7 5 6 5c2 0 3.3 1.1 4 2.2.7-1.1 2-2.2 4-2.2 3.300 0 5.100 3.400 3.600 6.800C19.500 16.400 12 21 12 21Z" />
          </svg>
          <span>Служба турботи</span>
        </button>
      </div>

      {careOpen && <FeedbackModal onClose={() => setCareOpen(false)} />}

      <PopularBrands />

      {/* Home's own discovery grid stays a general fashion-first feed —
          Women/Men/Kids tiles and department chips above are navigation
          into SearchResults (the real fashion browse screen), not filters
          on this feed. See ShoppingRefinementBar.js. */}
      <Feed shoppingOnly />
      </div>

      <div className="home__aside">
      {isDesktopShell && (
        <div className="home__chats-stack">
          <HomeChatsPanel />
          <HomeNotificationsPanel />
        </div>
      )}

      {isDesktopShell && (
        <div className="home__feedback">
          <button
            type="button"
            className={`home__feedback-toggle ${feedbackOpen ? 'is-open' : ''}`}
            aria-expanded={feedbackOpen}
            onClick={() => setFeedbackOpen((v) => !v)}
          >
            <span>Зворотний зв'язок</span>
            <svg
              className="home__chevron"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>

          <div className={`home__feedback-body ${feedbackOpen ? 'is-open' : 'is-closed'}`}>
            <FeedbackBanner />
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Home;